import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  useClearMachineryDirectiveEPlan,
  useGetMachineryDirectiveEPlan,
  useUpdateMachineryDirectiveEPlan,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import type { ProductSource } from '@/shared/hooks/api/productSource'
import { Button } from '@/components/ui/button'
import { useMachineryEPlanUpload } from '../hooks/useMachineryEPlanUpload'
import { EPlanFileUploadStatus } from '../steps/settings/EPlanFileUploadStatus'
import { EPlanSelectDialog } from '../steps/settings/EPlanSelectDialog'
import { EPlanUploadDialog } from '../steps/settings/EPlanUploadDialog'

type OnboardingEPlanStepProps = {
  productId: string
  complianceId: string
  onContinueWithEPlan: () => void
  onContinueWithoutEPlan: () => void
}

function getEPlanSourceLabel(source: ProductSource): string {
  if (source.classification == null) {
    return source.name
  }
  const classificationLabel = source.classification.replace(/_/g, ' ')
  return `${source.name} (${classificationLabel})`
}

export function OnboardingEPlanStep({
  productId,
  complianceId,
  onContinueWithEPlan,
  onContinueWithoutEPlan,
}: OnboardingEPlanStepProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const { data: ePlanData } = useGetMachineryDirectiveEPlan(complianceId)
  const updateEPlan = useUpdateMachineryDirectiveEPlan(complianceId, productId)
  const clearEPlan = useClearMachineryDirectiveEPlan(complianceId, productId)
  const { handleFileSelect, uploadingSourceIds } = useMachineryEPlanUpload({
    productId,
    complianceId,
  })

  const [sourceDialogOpen, setSourceDialogOpen] = useState(false)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [isClearingSelection, setIsClearingSelection] = useState(false)

  const assignedSource = ePlanData?.productSource ?? null
  const selectedSourceLabel =
    assignedSource != null ? getEPlanSourceLabel(assignedSource) : ''
  const isSourceUploading =
    assignedSource != null && uploadingSourceIds.has(assignedSource.id)

  const handleSourceConfirm = async (source: ProductSource) => {
    try {
      await updateEPlan.mutateAsync({ productSourceId: source.id })
    } catch {
      toast.error(t('settings.ePlan.linkError'))
    }
  }

  const handleClearSelectedSource = async () => {
    if (assignedSource == null) {
      return
    }

    setIsClearingSelection(true)
    try {
      await clearEPlan.mutateAsync()
    } catch {
      toast.error(t('settings.ePlan.clearSelectedSourceError'))
    } finally {
      setIsClearingSelection(false)
    }
  }

  return (
    <div className='space-y-6'>
      <div className='space-y-4'>
        <h2 className='text-lg font-semibold tracking-tight'>
          {t('onboarding.ePlan.title')}
        </h2>
        <p className='leading-relaxed text-muted-foreground'>
          {t('onboarding.ePlan.intro')}
        </p>
      </div>
      <div className='space-y-6'>
        {assignedSource != null && (
          <div className='space-y-3'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <div className='flex min-w-0 items-center gap-2'>
                <p className='font-medium'>{selectedSourceLabel}</p>
                <EPlanFileUploadStatus
                  productId={productId}
                  sourceId={assignedSource.id}
                  ingestionStatus={assignedSource.ingestionStatus}
                  ingestionStage={assignedSource.ingestionStage}
                  ingestionProgress={assignedSource.ingestionProgress}
                  ingestionErrorCode={assignedSource.ingestionErrorCode}
                  ingestionPartialResult={assignedSource.ingestionPartialResult}
                  ingestionMissingPages={assignedSource.ingestionMissingPages}
                  fileUploadStatus={assignedSource.fileUploadStatus}
                  filePathInBucket={assignedSource.filePathInBucket}
                  isUploading={isSourceUploading}
                />
              </div>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                disabled={isClearingSelection}
                onClick={() => {
                  void handleClearSelectedSource()
                }}
              >
                {t('settings.ePlan.clearSelectedSource')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {assignedSource == null && (
        <div className='space-y-4'>
          <div className='flex flex-wrap gap-2'>
            <Button
              type='button'
              variant='outline'
              onClick={() => setSourceDialogOpen(true)}
            >
              {t('settings.ePlan.assignFlow.selectFromSources')}
            </Button>
            <Button
              type='button'
              variant='outline'
              onClick={() => setUploadDialogOpen(true)}
            >
              {t('settings.ePlan.assignFlow.upload')}
            </Button>
          </div>
          <p className='text-sm text-muted-foreground'>
            {t('onboarding.ePlan.continueWithoutUploadHelper')}
          </p>
        </div>
      )}

      <div className='flex justify-end'>
        <Button
          type='button'
          disabled={isClearingSelection}
          onClick={() => {
            if (assignedSource != null) {
              onContinueWithEPlan()
            } else {
              onContinueWithoutEPlan()
            }
          }}
        >
          {t('onboarding.ePlan.continueButton')}
        </Button>
      </div>

      <EPlanSelectDialog
        productId={productId}
        open={sourceDialogOpen}
        onOpenChange={setSourceDialogOpen}
        selectedSourceId={assignedSource?.id ?? null}
        onConfirm={(source) => {
          void handleSourceConfirm(source)
        }}
      />

      <EPlanUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onFileSelect={handleFileSelect}
      />
    </div>
  )
}
